/**
 * Template for plan upgrade / renewal dialogs.
 * Layout: header (icon + title) → description → optional progress bar → footer.
 * Architectural Context: Pure UI template. No business logic; content via props.
 *
 * @param progress - When set, shows usage progress bar (usage_limit_exceeded variant)
 * @param origin - GTM origin for button tracking
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Dialog } from '../../organisms/Dialog';
import { Button } from '../../atoms/Button';
import { Card, Typography } from '../../atoms';
import { ProgressBar } from '../../molecules';
import { getGTMAttributes, createGTMTracking } from '../../../../utils/gtm/gtm-tracking';
import type { PlanUpgradeDialogTemplateProps } from '../PlanUpgradeDialogTemplate/PlanUpgradeDialogTemplate.types';

export const PlanUpgradeDialogTemplate: React.FC<PlanUpgradeDialogTemplateProps> = ({
  open,
  onOpenChange,
  title,
  description,
  bodyContent,
  progress,
  primaryCtaLabel,
  primaryCtaIcon,
  onPrimaryClick,
  origin,
  titleId,
  maxWidth = 'lg',
}) => {
  const dialogDescription = `${title}. ${description}`;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={dialogDescription}
      maxWidth={maxWidth}
      closeOnBackdropClick={true}
      titleId={titleId}
    >
      <Dialog.Header
        title={title}
        icon={<AlertCircle />}
        iconProps={{ color: 'error', ariaHidden: true }}
        showCloseButton={true}
        closeButtonProps={{
          ...getGTMAttributes(
            createGTMTracking('UPGRADE_PLAN', { origin, target: 'close' })
          ),
          'aria-label': 'Close dialog',
        }}
      />

      <Dialog.Body className="space-y-4 sm:space-y-5 pb-1">
        <Typography as="p" variant="body-sm" weight='medium' color="secondary" className="!mt-0">
          {description}
        </Typography>
        {progress && (
          <Card variant="subtle" padding="md">
            <ProgressBar
              value={progress.used}
              max={progress.limit}
              variant="inline-label"
              label={progress.label}
              valueLabel={`${progress.used} / ${progress.limit} used`}
              aria-label={`${progress.label} usage`}
            />
          </Card>
        )}
        {bodyContent}
      </Dialog.Body>

      <Dialog.Footer align="right">
        <Button
          {...getGTMAttributes(
            createGTMTracking('UPGRADE_PLAN', { origin, target: 'cancel' })
          )}
          label="Not now"
          type="outlined"
          intent="secondary"
          onClick={() => onOpenChange(false)}
        />
        <Button
          {...getGTMAttributes(
            createGTMTracking('UPGRADE_PLAN', { origin, target: 'upgrade' })
          )}
          label={primaryCtaLabel}
          type="contained"
          intent="secondary"
          rightIcon={primaryCtaIcon}
          onClick={onPrimaryClick}
        />
      </Dialog.Footer>
    </Dialog>
  );
};
